/*
  Tearstones review album motion
  Owns seamless, compositor-driven review motion and reading pauses. The shared
  carousel, review content, native static scrolling and other galleries stay intact.
*/
(() => {
  function queueAlbum(section) {
    if (section.dataset.scrollReady || section.dataset.scrollPending) return;
    if (window.Shopify?.designMode === true) {
      startAlbum(section);
      return;
    }
    section.dataset.scrollPending = "true";
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const cancel = () => {
      observer.disconnect();
      controller.abort();
      delete section.dataset.scrollPending;
    };
    const start = () => {
      cancel();
      if (section.isConnected) startAlbum(section);
    };
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) start();
    }, { rootMargin: "600px 0px" });
    observer.observe(section);
    section.addEventListener("focusin", start, options);
    document.addEventListener("shopify:section:unload", (event) => {
      if (event.target.contains(section)) cancel();
    }, options);
  }

  function startAlbum(section) {
    if (section.dataset.scrollReady) return;
    const carousel = section.querySelector("theme-carousel");
    const track = section.querySelector("[data-carousel-track]");
    if (!carousel || !track) return;
    section.dataset.scrollReady = "true";
    const originals = [...track.children];
    if (originals.length < 2) return;
    const copies = originals.map((item) => {
      const copy = item.cloneNode(true);
      copy.setAttribute("aria-hidden", "true");
      copy.setAttribute("inert", "");
      copy.dataset.reviewCopy = "true";
      copy.removeAttribute("data-carousel-item");
      copy.removeAttribute("data-shopify-editor-block");
      copy.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
      track.append(copy);
      return copy;
    });
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const listen = (node, type, handler) => node.addEventListener(type, handler, options);
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    const speed = Number(carousel.dataset.reviewScrollSpeed) || 20;
    let animation;
    let duration = 0;
    let visible = false;
    let hovered = matchMedia("(hover: hover)").matches && section.matches(":hover");
    let focused = section.contains(document.activeElement);
    let pointer;
    let pointerStart = 0;
    let startTime = 0;
    let resumeTimer;
    let measureFrame;
    const sync = () => {
      section.dataset.smoothStatic = String(reduced.matches);
      if (!animation) return;
      if (reduced.matches) {
        animation.pause();
        animation.currentTime = 0;
      } else if (visible && !document.hidden && !hovered && !focused &&
          pointer === undefined && resumeTimer === undefined) {
        animation.play();
      } else {
        animation.pause();
      }
    };
    const measure = () => {
      const progress = duration && animation ? (Number(animation.currentTime) % duration) / duration : 0;
      animation?.cancel();
      section.dataset.smoothStatic = "false";
      const distance = copies[0].offsetLeft - originals[0].offsetLeft;
      if (distance <= 0) return;
      duration = distance / speed * 1000;
      track.scrollLeft = 0;
      animation = track.animate([
        { transform: "translate3d(0, 0, 0)" },
        { transform: "translate3d(-" + distance + "px, 0, 0)" },
      ], { duration, iterations: Infinity, easing: "linear" });
      animation.pause();
      animation.currentTime = progress * duration;
      originals.forEach((item) => {
        item.removeAttribute("aria-hidden");
        item.removeAttribute("inert");
      });
      sync();
    };
    const scheduleMeasure = () => {
      cancelAnimationFrame(measureFrame);
      measureFrame = requestAnimationFrame(() => {
        measureFrame = requestAnimationFrame(measure);
      });
    };
    const readingPause = () => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        resumeTimer = undefined;
        sync();
      }, 2500);
      sync();
    };
    listen(section, "pointerenter", (event) => {
      if (event.pointerType === "mouse") hovered = true;
      sync();
    });
    listen(section, "pointerleave", () => { hovered = false; sync(); });
    listen(section, "focusin", () => { focused = true; sync(); });
    listen(section, "focusout", (event) => { focused = section.contains(event.relatedTarget); sync(); });
    listen(track, "pointerdown", (event) => {
      pointer = event.pointerId;
      pointerStart = event.clientX;
      startTime = Number(animation?.currentTime) || 0;
      sync();
    });
    listen(window, "pointermove", (event) => {
      if (event.pointerId !== pointer || !animation || reduced.matches || !duration) return;
      const time = startTime - (event.clientX - pointerStart) / speed * 1000;
      animation.currentTime = ((time % duration) + duration) % duration;
    });
    const release = (event) => {
      if (event.pointerId !== pointer) return;
      pointer = undefined;
      readingPause();
    };
    listen(window, "pointerup", release);
    listen(window, "pointercancel", release);
    listen(track, "wheel", readingPause);
    listen(document, "visibilitychange", sync);
    reduced.addEventListener("change", scheduleMeasure, options);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      sync();
    });
    observer.observe(section);
    const resize = new ResizeObserver(scheduleMeasure);
    resize.observe(track);
    listen(document, "shopify:section:unload", (event) => {
      if (!event.target.contains(section)) return;
      controller.abort();
      observer.disconnect();
      resize.disconnect();
      animation?.cancel();
      cancelAnimationFrame(measureFrame);
      clearTimeout(resumeTimer);
    });
    scheduleMeasure();
  }
  customElements.whenDefined("theme-carousel").then(() => {
    document.querySelectorAll(".ts-photo-reviews[data-slow-scroll]").forEach(queueAlbum);
    document.addEventListener("shopify:section:load", (event) => {
      event.target.querySelectorAll(".ts-photo-reviews[data-slow-scroll]").forEach(queueAlbum);
    });
  });
})();
